/*
 * Copyright 2023-2025 Steve Xiao (stevexmh@qq.com) and contributors.
 *
 * 本源代码文件是属于 AMLL TTML Tool 项目的一部分。
 * This source code file is a part of AMLL TTML Tool project.
 * 本项目的源代码的使用受到 GNU GENERAL PUBLIC LICENSE version 3 许可证的约束，具体可以参阅以下链接。
 * Use of this source code is governed by the GNU GPLv3 license that can be found through the following link.
 *
 * https://github.com/Steve-xmh/amll-ttml-tool/blob/main/LICENSE
 */

import type { OptimizeLyricOptions } from "@applemusic-like-lyrics/core";
import type {
	LyricLine as AMLLLyricLine,
	LyricWord as AMLLLyricWord,
} from "@applemusic-like-lyrics/lyric";
import { uid } from "uid";

export interface TTMLMetadata {
	key: string;
	value: string[];
	error?: boolean;
	autoSuggested?: boolean;
}

export interface TTMLVocalTag {
	key: string;
	value: string;
}

export interface TTMLAgent {
	id: string;
	type: "person" | "group" | "other";
	names: string[];
}

export interface TTMLLyric {
	metadata: TTMLMetadata[];
	lyricLines: LyricLine[];
	vocalTags?: TTMLVocalTag[];
	agents: TTMLAgent[];
	optimizeOptions?: OptimizeLyricOptions;
	/** 整个歌词的语言代码 */
	lyricLang?: string;
	/**
	 * 当文件没有定义 xml:lang 而使用默认值时这个值为 true
	 * 此时读取翻译时不应该触发自动匹配逐字翻译的逻辑
	 */
	autoLang?: boolean;
	/**
	 * @description 存储文件中出现的但不在预设列表中的自定义 song-part 值
	 */
	customSongParts?: string[];
}

export interface LyricWordBase {
	startTime: number;
	endTime: number;
	word: string;
	emptyBeat?: number;
}

export interface LyricWord extends AMLLLyricWord {
	// 用来确定唯一一个单词的标识符，导出时不会保存
	id: string;
	startTime: number;
	endTime: number;
	word: string;
	obscene: boolean;
	emptyBeat: number;
	romanWord: string;
	romanWarning?: boolean;
	ruby?: LyricWordBase[];
	rubyPhraseStart: boolean;
}

export interface TTMLRomanWord {
	startTime: number;
	endTime: number;
	text: string;
	/** @description 该音节后是否有空格 */
	hasSpaceAfter?: boolean;
}

export interface TTMLTranslationWord {
	startTime: number;
	endTime: number;
	text: string;
	/** @description 该音节后是否有空格 */
	hasSpaceAfter?: boolean;
}

/**
 * @description 带自动填充标记的语言代码数据
 * 用于标识该语言代码是否是自动填充的默认值
 */
export interface TTMLLangData<T> {
	/** @description 语言代码数据 */
	data: T;
	/** @description 是否是自动填充的默认值 */
	isAutoFilled?: boolean;
}

export const newLyricWord = (): LyricWord => ({
	id: uid(),
	startTime: 0,
	endTime: 0,
	word: "",
	obscene: false,
	emptyBeat: 0,
	romanWord: "",
	rubyPhraseStart: false,
});

export interface LyricLine extends AMLLLyricLine {
	// 用来确定唯一一个行的标识符，导出时不会保存
	id: string;
	words: LyricWord[];
	translatedLyric: string;
	romanLyric: string;
	isBG: boolean;
	isDuet: boolean;
	startTime: number;
	endTime: number;
	ignoreSync: boolean;
	/**
	 * @description 用于记录时间链接前的原始时间值，便于取消链接时恢复
	 */
	endTimeLink?: {
		/**
		 * @description 该行原始的结束时间
		 */
		originalEndTime: number;
		/**
		 * @description 下一行原始的开始时间，没有则为 null
		 */
		originalNextStartTime: number | null;
	};
	vocal?: string[];
	translatedLyricByLang?: Record<string, TTMLLangData<string>>;
	romanLyricByLang?: Record<string, TTMLLangData<string>>;
	wordRomanizationByLang?: Record<string, TTMLLangData<TTMLRomanWord[]>>;
	wordTranslationByLang?: Record<string, TTMLLangData<TTMLTranslationWord[]>>;
	/**
	 * @description 存储该行的 songPart 信息（来自父级 div 的 itunes:song-part 属性）
	 */
	songPart?: string;
	/**
	 * @description 存储该行的 agent 信息（来自 ttm:agent 属性）
	 */
	agent?: string;
	/**
	 * @description 该行是否为从右到左（RTL）显示
	 */
	isRtl?: boolean;
	/**
	 * @description 该行的 itunes:key
	 * - 主行: "L0", "L1", "L2"...
	 * - 背景行: "B0", "B1", "B2"...
	 */
	itunesKey?: string;
}

export const newLyricLine = (): LyricLine => ({
	id: uid(),
	words: [],
	translatedLyric: "",
	romanLyric: "",
	isBG: false,
	isDuet: false,
	startTime: 0,
	endTime: 0,
	ignoreSync: false,
	vocal: [],
	isRtl: false,
});
